


export interface RecorridoPunto {
  x: number;
  y: number;
}

export interface RecorridoSolicitud {
  iglesia: RecorridoPunto;
  /**
   * Iglesia donde se hace estación de penitencia. Solo en los días de vísperas
   * (miércoles y jueves de pasión, viernes de dolores y sábado de pasión), que
   * no tienen Carrera Oficial.
   */
  estacion?: RecorridoPunto | null;
  ida: RecorridoPunto[];
  vuelta: RecorridoPunto[];
  estado: 'pendiente' | 'aprobado' | 'rechazado';
  color?: string;
  enviadoPor: string;
  enviadoAt?: any;
  revisadoPor?: string;
  revisadoAt?: any;
  motivoRechazo?: string;
}

/** Un punto del cuadro de horarios: la calle o lugar y qué pinta ahí. */
export interface ItinerarioPunto {
  calle: string;
  tipo: 'itinerario' | 'carreraOficial' | 'salida' | 'catedral' | 'entrada';
}

/**
 * Estado de la solicitud del cuadro de horarios que manda el hermano mayor.
 * Los puntos NO viven aquí: viven en `itinerario`, que es lo que pinta el libro,
 * para que el admin pueda seguir retocándolos cuando quiera sin tener que
 * mantener dos copias sincronizadas.
 */
export interface HorarioSolicitud {
  estado: 'pendiente' | 'aprobado' | 'rechazado';
  enviadoPor: string;
  enviadoAt?: any;
  revisadoPor?: string;
  revisadoAt?: any;
  motivoRechazo?: string;
}

export interface DiaSSHermandadDetalles {
  hermandadId: string;
  pasos: '1 PASO' | '2 PASOS';
  acompanamientoMusical: string[];
  numeroDeNazarenos: number;
  /** Cuadro de horarios, indexado por la hora `HH:MM` (rejilla de 10 minutos). */
  itinerario: Record<string, ItinerarioPunto>;
  /** Solicitud del cuadro de horarios; los puntos están en `itinerario`. */
  horario?: HorarioSolicitud;
  recorrido?: RecorridoSolicitud;
  /** Foto de la hermandad para el libro (va a la izquierda de la tarjeta). */
  fotoUrl?: string;
  /**
   * Descripción del hábito nazareno (túnica, antifaz, cíngulo…). Sustituye al
   * antiguo campo "Info" de la hermandad en el libro y se escribe desde
   * /jartinet/admin/programa-ss26.
   */
  habito?: string;
  /** Imagen del nazareno; va a la derecha de la tarjeta, frente a `fotoUrl`. */
  nazarenoUrl?: string;
  /**
   * Segunda imagen de nazareno (hermandades con dos hábitos distintos). Si está,
   * las dos se pintan en el mismo hueco que ocuparía una sola, una desplazada a
   * la izquierda y la otra a la derecha, solapándose.
   */
  nazarenoUrl2?: string;
}


export interface DiaSS {
  id: string;
  nombredia: string;
  tipo: 'visperas' | 'semanasanta';
  orden: number;
  fecha: any;
  hermandades: string[] | DiaSSHermandadDetalles[];
  portadaUrl?: string;
}

/**
 * Un punto de calibración del mapa: qué coordenada de Minecraft hay en ese
 * punto de la imagen (en % de su ancho y alto). Con dos puntos se sabe cuántos
 * bloques mide un 1 % del mapa, y como 1 bloque = 1 metro se puede medir un
 * recorrido en kilómetros.
 */
export interface MapaCalibPunto {
  xPct: number;
  yPct: number;
  mcX: number;
  mcZ: number;
}

export interface ProgramaSSConfig {
  portadaLibroUrl?: string;
  carreraOficial?: RecorridoPunto[];
  catedralPunto?: RecorridoPunto;
  /**
   * Copia de la calibración de `mod_map_config/main`, que es de lectura
   * solo-admin: el libro lo ve todo el mundo (incluso sin sesión), así que la
   * escala se duplica aquí. El panel de admin la refresca al abrirse.
   */
  mapCalib?: MapaCalibPunto[];
  updatedAt?: any;
}



export interface HighlightStoryGroup {
  id: string;
  name: string;
  coverUrl: string;
  storyIds: string[];
  createdAt: any;
}

export interface Banda {
  id: string;
  nombreCompleto: string;
  escudoUrl: string;
  foto: string;
  fundacion: number;
  directores: string[];
  repertorio: string[];
  colorPrimario: string;
  colorSecundario: string;
  tipo: 'Agrupación Musical' | 'Cornetas y Tambores' | 'Banda de Música' | 'Capilla';
  componentes?: number;
  info?: string;
  bio?: string;
  pinnedPosts?: { 
    left?: string;
    center?: string;
    right?: string;
  };
  highlightGroups?: HighlightStoryGroup[];
  updatedAt?: any;
  updatedBy?: string;
}

export interface Organizacion {
  id: string;
  nombreCompleto: string;
  escudoUrl: string;
  gestores: string[];
  colorPrimario: string;
  colorSecundario: string;
  bio?: string;
  pinnedPosts?: { 
    left?: string;
    center?: string;
    right?: string;
  };
  highlightGroups?: HighlightStoryGroup[];
  updatedAt?: any;
  updatedBy?: string;
}

export interface JartinetHermandad {
  id: string;
  nombre: string;
  nombreCompleto: string;
  escudoUrl: string;
  foto?: string;
  titulos?: string[];
  titulares: string;
  fundacion: number;
  hermanosMayores: string[];
  hermanos: string[];
  colorPrimario: string;
  colorSecundario: string;
  sede: string;
  tipo: 'Hermandad' | 'Grupo Parroquial' | 'Hermandad de Gloria' | 'Pro-Hermandad';
  info?: string;
  /**
   * Día de salida. Es el nombre del día del programa de Semana Santa en el que
   * está programada (`dias_ss`), así que es texto libre: lo escribe la Cloud
   * Function `admin_setHermandadDiaSS` para que los dos lados no se desincronicen.
   */
  dia?: string | null;
  bio?: string;
  libroReglasUrl?: string;
  pinnedPosts?: { 
    left?: string;
    center?: string;
    right?: string;
  };
  highlightGroups?: HighlightStoryGroup[];
  updatedAt?: any;
  updatedBy?: string;
}

export interface UserProfile {
    id: string; // The UID
    nickname?: string;
    profileImageUrl?: string;
    email?: string;
    role?: 'user' | 'admin';
    createdAt?: any;
    bio?: string;
    banned?: boolean;
    banReason?: string;
    bannedAt?: any;
    discordId?: string;
    minecraftId?: string;
    pinnedPosts?: {
      left?: string;
      center?: string;
      right?: string;
    };
}

export interface JoinRequest {
    id: string; // Corresponds to the user's UID
    status?: 'pending' | 'approved' | 'rejected' | 'preparing_approval';
    userId: string;
    solicitedAt: string; // ISO 8601 date string
}

export interface SocialStory {
  id: string;
  authorType: 'hermandad' | 'banda' | 'organizacion' | 'user';
  authorId: string;
  authorName: string;
  authorShieldUrl: string;
  mediaUrl: string | string[];
  mediaType: 'image' | 'video';
  text?: string;
  spotifyUrl?: string;
  spotifyTrackName?: string;
  spotifyStartTime?: number;
  spotifyEndTime?: number;
  audioPreviewUrl?: string; // URL directa de la preview (elegida con el buscador)
  createdAt: any; // Can be Timestamp, Date, or string
  expiresAt: any; // Can be Timestamp, Date, or string
  viewedBy: string[];
  linkedPostId?: string;
}

export interface HighlightStory {
  id: string;
  title: string;
  coverUrl: string;
  storyIds: string[];
  createdAt: any;
}


export interface FeaturedStory {
  id: string;
  name: string;
  coverImageUrl: string;
  storyIds: string[];
  createdAt: any;
}

export interface SocialComment {
  id: string;
  userId: string;
  username: string;
  profileImageUrl: string;
  text: string;
  createdAt: any;
}

export interface SocialPost {
  id: string;
  authorType: 'hermandad' | 'banda' | 'organizacion' | 'user';
  authorId: string;
  authorName: string;
  authorShieldUrl: string;
  title: string;
  content: string;
  mediaUrl?: string | string[];
  mediaType?: 'image' | 'video';
  spotifyUrl?: string;
  spotifyStartTime?: number;
  spotifyEndTime?: number;
  spotifyTrackName?: string;
  audioPreviewUrl?: string; // URL directa de la preview (elegida con el buscador)
  creatorId: string;
  createdAt: any; // Can be Timestamp or string
  updatedAt?: any; // Can be Timestamp or string
  likes?: string[];
  comments?: SocialComment[];
  isPinned?: 'left' | 'center' | 'right' | false;
  
  // Collaboration fields
  collaborationStatus?: 'pending' | 'accepted' | 'rejected';
  collaboratorId?: string;
  collaboratorName?: string;
  collaboratorShieldUrl?: string;
}
    
export type SocialProfile = (JartinetHermandad & { type: 'hermandad' }) | (Banda & { type: 'banda' }) | (Organizacion & { type: 'organizacion' });

export type CalendarEventTipo = 'normal' | 'procesion' | 'cultos';

export interface CalendarEvent {
    id: string;
    title: string;
    start: Date;
    end: Date;
    allDay: boolean;
    description?: string;
    color?: string;
    /**
     * 'procesion' se señala junto al número del día (y tiñe la celda);
     * 'cultos' va en su propia sección al pie de la celda.
     */
    tipo?: CalendarEventTipo;
    /**
     * Hermandad organizadora. Si está puesta, el evento se pinta con el color
     * configurado para esa hermandad y usa su escudo como icono.
     */
    hermandadId?: string;
}

/** Colores de evento por hermandad, configurables desde el calendario. */
export interface CalendarioConfig {
    hermandadColors?: Record<string, string>;
}

export interface Contract {
  id: string;
  emitterId: string; // e.g., 'hermandad:id123'
  emitterName: string;
  emitterShieldUrl: string;
  recipientId: string; // e.g., 'banda:id456'
  recipientName: string;
  recipientShieldUrl: string;
  concept: string;
  content: string;
  status: 'pending' | 'signed' | 'rejected';
  createdAt: any;
  signedAt?: any;
  emitterSignatureUrl?: string; // URL to the signature image
  recipientSignatureUrl?: string; // URL to the signature image
}

export interface PeriodicoArticle {
  id: string;
  title: string;
  subtitle: string;
  content: string;
  authorName: string;
  imageUrl: string;
  category: string;
  columns?: number; // distribución del cuerpo en columnas (1, 2 o 3). Default 1.
  createdAt: any; // Timestamp or string
}

export interface Aviso {
  id: string;
  title: string;
  content: string;
  createdAt: any; // Timestamp or string
}

export interface Noticia {
  id: string;
  title: string;
  content: string;
  date: any; // Timestamp or string
  imageUrl?: string;
}

export interface ModUpdate {
  id: string;
  title: string;
  description: string;
  date: any; // Timestamp or string
  /** @deprecated legacy: la URL ya no se guarda en el doc público (ver mod_releases) */
  downloadUrl?: string;
  version?: string;
  accentColor?: string;
  badgeText?: string;
  featured?: boolean;
  locked?: boolean;
  hasDownload?: boolean;
  downloadType?: 'file' | 'external' | 'none';
}

export interface MapPin {
  id: number;
  name: string;
  description: string;
  coords: [number, number];
}

// ─── TIENDA DE PERSONALIZABLES ──────────────────────────────────────────────
// Antes "Packs". Se compran con DINERO REAL (Bizum o transferencia) fuera de la
// plataforma: la web solo gestiona la solicitud, el estado del pedido y el chat.
// La colección sigue llamándose `packs_products` para no perder el catálogo.

export interface PackProduct {
  id: string;
  name: string;
  description: string;
  /**
   * Precio en euros. Es el importe que se le pide al usuario en el concepto del
   * Bizum / la transferencia, así que tiene que ser un número.
   */
  priceEur: number;
  /** Texto libre heredado ("12€", "Precio variable"). Solo se usa como respaldo
   *  de visualización en productos antiguos sin `priceEur`. */
  price?: string;
  imageUrl?: string;
  /** Galería opcional: fotos adicionales del producto. */
  images?: string[];
  category?: string;
  includes?: string[];    // elementos que incluye (opcional)
  /** -1 = ilimitado. 0 = agotado. Se descuenta cuando el admin ACEPTA. */
  stock?: number;
  active?: boolean;
  featured?: boolean;
  order?: number;
  createdAt?: any;
  updatedAt?: any;
}

/**
 * Ciclo de vida de un pedido de personalizable:
 *
 *   pendiente ──► rechazado                       (el admin lo deniega + motivo)
 *      │
 *      └────────► aceptado ──► pago_reportado ──► en_proceso ──► terminado
 *                (reserva stock) (el usuario     (el admin      ──► en_mod
 *                                 dice que pagó)  confirma el       ──► entregado
 *                                                 cobro; se abre
 *                                                 el chat)
 *
 * `cancelado` es terminal y puede llegar desde cualquier estado (devuelve stock).
 */
export type PackOrderStatus =
  | 'pendiente'
  | 'rechazado'
  | 'aceptado'
  | 'pago_reportado'
  | 'en_proceso'
  | 'terminado'
  | 'en_mod'
  | 'entregado'
  | 'cancelado';

export type PackPaymentMethod = 'bizum' | 'transferencia';

export interface PackOrderEvent {
  status: PackOrderStatus | 'solicitado';
  at: any;
  note?: string;
}

export interface PackOrder {
  id: string;
  /** Código legible tipo `PZ-7F3A2C`: es lo que va en el concepto del pago. */
  code: string;
  uid: string;
  userName?: string;
  /** Hermandad para la que se pide el personalizable (el solicitante es HM). */
  hermandadId: string;
  hermandadNombre: string;
  hermandadEscudoUrl?: string;
  hermandadColor?: string;
  productId: string;
  productName: string;
  productImageUrl?: string;
  /** Importe en euros congelado al solicitar: si el catálogo sube de precio,
   *  el pedido ya enviado no cambia. */
  priceEur: number;
  /** Descripción de cómo quiere el producto (markdown). */
  message: string;
  /** Fotos de referencia subidas con la solicitud. */
  images?: string[];
  status: PackOrderStatus;
  /** Motivo que escribe el admin al rechazar. */
  rejectionReason?: string;
  paymentMethod?: PackPaymentMethod | null;
  /** El chat se abre cuando el admin confirma el cobro. */
  chatOpen?: boolean;
  /** Si al aceptar se descontó una unidad del stock (para devolverla al cancelar). */
  stockReserved?: boolean;
  createdAt?: any;
  acceptedAt?: any;
  rejectedAt?: any;
  paymentReportedAt?: any;
  paidAt?: any;
  finishedAt?: any;
  inModAt?: any;
  deliveredAt?: any;
  cancelledAt?: any;
  termsAcceptedAt?: any;
  tracking?: PackOrderEvent[];
  /** Novedades sin leer para cada lado (cambios de estado y mensajes de chat).
   *  No dependen de `lastSeen`: se limpian al abrir el pedido concreto. */
  unreadUser?: number;
  unreadAdmin?: number;
  lastMessageAt?: any;
}

export interface PackOrderMessage {
  id: string;
  orderId: string;
  authorId: string;
  authorName: string;
  /** Los mensajes del equipo se pintan como "Administración". */
  fromAdmin: boolean;
  text: string;
  images?: string[];
  at?: any;
}

/** Ajustes públicos de la tienda (`app_settings/tienda`). */
export interface PacksShopConfig {
  /** Si está cerrada no se admiten solicitudes nuevas. */
  open?: boolean;
  /** Aviso destacado en la cabecera de la tienda. */
  announcement?: string;
  /** Términos y condiciones que hay que aceptar al solicitar (markdown). */
  terms?: string;
  updatedAt?: any;
}

/**
 * Datos de cobro (`packs_config/main`). NO son públicos: solo los sirve la
 * Cloud Function `packsShop_getPaymentInfo` al dueño de un pedido ya aceptado.
 */
export interface PacksPaymentInfo {
  bizumNumber?: string;
  bizumHolder?: string;
  iban?: string;
  ibanHolder?: string;
  bankName?: string;
  /** Instrucciones extra que se enseñan junto a los datos de pago. */
  notes?: string;
  updatedAt?: any;
}

// ─── CONTABILIDAD ───────────────────────────────────────────────────────────
// Vive entera en Firestore (`packs_ledger` + `packs_expenses`), no en una hoja
// externa. Las dos son solo-admin.

/**
 * Un ingreso. Se crea SOLO cuando el admin confirma que ha recibido el pago
 * (`packsShop_adminSetStatus` con `paid`), o a mano desde el panel.
 */
export interface PackLedgerEntry {
  id: string;
  /** Fecha en que entró el dinero. */
  fechaPago: any;
  /** Nº de pedido (`PZ-XXXXXX`). Vacío en las filas manuales sueltas. */
  code: string;
  /** Id del pedido, si viene de uno. Es también el id del documento. */
  orderId?: string;
  /** Nombre de la cuenta de Jartinet del cliente. */
  cliente: string;
  cofradia: string;
  metodo: PackPaymentMethod | 'otro';
  producto: string;
  /** Importe en euros. */
  precio: number;
  /** Estado del pedido. Se mantiene en sync cuando el pedido avanza. */
  estado: PackOrderStatus | string;
  /** true si la fila la añadió un admin a mano. */
  manual?: boolean;
  createdAt?: any;
  updatedAt?: any;
}

/** Un gasto. Solo concepto e importe. */
export interface PackExpense {
  id: string;
  concepto: string;
  /** Importe en euros (positivo). */
  precio: number;
  /** Opcional: si no se pone, se usa la de creación para las gráficas. */
  fecha?: any;
  createdAt?: any;
  updatedAt?: any;
}

export interface SportsMatch {
  id: string;
  title: string;
  date: string;        // fecha/hora del partido (texto)
  team1: string;
  team2: string;
  oddsHome: number;    // cuota victoria team1
  oddsDraw: number;    // cuota empate
  oddsAway: number;    // cuota victoria team2
  status: 'open' | 'closed' | 'settled';
  result: 'home' | 'draw' | 'away' | null;
  createdAt?: any;
}

export interface SportsBet {
  id: string;
  userId: string;
  matchId: string;
  matchTitle: string;
  team1: string;
  team2: string;
  selection: 'home' | 'draw' | 'away';
  odds: number;
  stake: number;
  potentialPayout: number;
  status: 'pending' | 'won' | 'lost';
  payout: number;
  placedAt?: any;
  settledAt?: any;
}

export type CityMapPinCategory = 'iglesia' | 'catedral' | 'zona' | 'edificio';

export interface CityMapPin {
  id: string;
  name: string;
  category: CityMapPinCategory;
  description: string;
  images: string[];
  x: number; // porcentaje (0-100) respecto al ancho de la imagen del mapa
  y: number; // porcentaje (0-100) respecto al alto de la imagen del mapa
  mapId?: string; // 'main' (principal) o id de un mapa secundario. Ausente = principal.
  createdAt?: any;
  updatedAt?: any;
}

export interface CityMapConfig {
  mapImageUrl: string;
  name?: string;
  description?: string;
  updatedAt?: any;
}

// Mapa secundario: tiene su propia foto, nombre, descripción y chinchetas.
export interface CitySecondaryMap {
  id: string;
  name: string;
  description: string;
  mapImageUrl: string;
  order?: number;
  createdAt?: any;
  updatedAt?: any;
}

export type BankAccountType = 'principal' | 'secundaria';

export interface BankAccount {
  id: string;
  accountName: string;
  accountType: BankAccountType;
  oro: number;
  plata: number;
  bronce: number;
  iban: string;
  ownerId: string;
  createdAt: any;
  // Solo cuentas PRINCIPAL (vinculadas a un jugador de Minecraft):
  mcUuid?: string;
  mcUsername?: string;
  // Solo cuentas SECUNDARIA (acceso compartido entre usuarios de Jartinet):
  sharedWith?: string[];
};

export interface Transaction {
  id: string;
  type: 'deposit' | 'withdrawal' | 'transfer_in' | 'transfer_out';
  amount: number;
  status?: 'PENDING' | 'APPLIED' | 'REJECTED';
  currency?: 'oro' | 'plata' | 'bronce';
  concept: string;
  date: any;
  from?: string;
  fromAccountName?: string;
  fromIban?: string;
  to?: string;
  toAccountName?: string;
  toIban?: string;
}

export interface PapeletaDeSitio {
    id: string;
    type: 'papeleta';
    hermandadId: string;
    hermandadNombre: string;
    hermandadEscudo: string;
    hermandadSede: string;
    
    hermanoId: string;
    diaDeSalida: string;
    cargo: string;
    hora: string;
    
    ornamentColor: string;
    titleColor: string;
    selloColor: string;
    selloOpacity: number;
    
    sentAt: any;
    status: 'enviada' | 'aceptada' | 'rechazada';
    updatedAt?: any;
}


export interface Carta {
    id: string;
    /** 'votacion' = la carta lleva una votación de hermandad para votar. */
    type?: 'carta' | 'votacion';
    emitterId: string; // hermandadId
    emitterName: string;
    recipientId: string; // userId
    subject: string;
    content: string;
    sentAt: any;
    isRead: boolean;
    hermandadId?: string;
    votacionId?: string;
}

// ─── VOTACIONES DE HERMANDAD ────────────────────────────────────────────────
// `jartinet_hermandades/{id}/votaciones/{id}`. Solo las escribe la CF
// `votaciones`; el documento completo (con los votos) solo lo lee el HM/admin.
export interface VotacionOpcion {
    id: string;
    texto: string;
}

export interface VotacionVoto {
    opcionId: string;
    nickname: string;
    at?: any;
}

export interface Votacion {
    id: string;
    titulo: string;
    descripcion?: string;
    opciones: VotacionOpcion[];
    estado: 'borrador' | 'abierta' | 'cerrada';
    /** uid → voto. */
    votos?: Record<string, VotacionVoto>;
    /** Hermanos convocados, congelados al empezar. */
    censo?: string[];
    createdAt?: any;
    createdBy?: string;
    startedAt?: any;
    closedAt?: any;
}

/** Lo que ve un hermano de una votación (sin los votos de los demás). */
export interface VotacionPublica {
    id: string;
    titulo: string;
    descripcion: string;
    opciones: VotacionOpcion[];
    estado: 'borrador' | 'abierta' | 'cerrada';
    miVoto: string | null;
    puedeVotar: boolean;
    hermandadNombre: string;
}

export interface Empresa {
  id: string;
  nombreCompleto: string;
  escudoUrl: string;
  gestores: string[];
  colorPrimario: string;
  colorSecundario: string;
  bio?: string;
  pinnedPosts?: {
    left?: string;
    center?: string;
    right?: string;
  };
  highlightGroups?: HighlightStoryGroup[];
  updatedAt?: any;
  updatedBy?: string;
}

export interface BankSettings {
  fixedDepositRates: { days: number, rate: number }[];
  cancellationPenalty: number;
  jartidexLastUpdated?: any;
}

export interface FixedTermDeposit {
  id: string;
  userId: string;
  accountId: string;
  amount: number;
  interestRate: number;
  termDays: number;
  payoutAmount: number;
  startDate: any;
  endDate: any;
  status: 'active' | 'completed' | 'cancelled';
}

export interface JartidexInvestment {
  id: string;
  userId: string;
  accountId: string;
  shares: number;
  purchasePrice: number;
  totalCost: number;
  purchaseDate: any;
  status: 'active' | 'sold';
  sellPrice?: number;
  sellDate?: any;
}

export interface JartidexHistory {
  id: string;
  date: any;
  value: number;
  changePercent: number;
  eventDescription?: string;
}

// ── JARTICARDS ──

export type JarticardType = 'titular' | 'banda' | 'recuerdos' | 'momentos' | 'estandarte' | 'iglesia' | 'paso_cristo' | 'paso_virgen' | 'escudos' | 'prime' | 'superprime';

export const JARTICARD_TYPES: JarticardType[] = ['titular', 'banda', 'recuerdos', 'momentos', 'estandarte', 'iglesia', 'paso_cristo', 'paso_virgen', 'escudos', 'prime', 'superprime'];

export const JARTICARD_TYPE_LABELS: Record<JarticardType, string> = {
  titular: 'Titular',
  banda: 'Banda',
  recuerdos: 'Recuerdos',
  momentos: 'Momentos',
  estandarte: 'Estandarte',
  iglesia: 'Iglesia',
  paso_cristo: 'Paso Cristo',
  paso_virgen: 'Paso Virgen',
  escudos: 'Escudos',
  prime: 'Prime',
  superprime: 'Súper Prime',
};

export interface Jarticard {
  id: string;
  number: number;
  type: JarticardType;
  name: string;
  imageUrl: string;
  crestUrl?: string | null;
  createdAt?: any;
  updatedAt?: any;
}

export interface JarticardSobre {
  id: string;
  name: string;
  description?: string;
  price: number; // en jartis de oro
  numCards: number;
  probabilities: Record<JarticardType, number>; // pesos por tipo (porcentaje)
  color?: string;
  imageUrl?: string | null;
  active?: boolean;
  stockPerUser?: number; // máximo de compras por usuario (0 = ilimitado)
  freeDaily?: boolean;   // sobre gratis reclamable cada 24 h
  createdAt?: any;
  updatedAt?: any;
}

// Documento de propiedad: users/{uid}/jarticards/{cardId}
export interface UserJarticard {
  cardId: string;
  count: number;
  firstObtainedAt?: any;
  lastObtainedAt?: any;
}

export interface MarketOffer {
  id: string;
  offererId: string;
  offererName: string;
  offeredCardId: string;
  status: 'pending' | 'accepted' | 'rejected';
  createdAt?: any;
}

export interface MarketListing {
  id: string;
  sellerId: string;
  sellerName: string;
  cardId: string;
  status: 'open' | 'completed' | 'cancelled';
  createdAt?: any;
  completedWith?: { offerId: string; buyerId: string; buyerCardId: string };
}

export interface BonoInversion {
  id: string;
  userId: string;
  accountId: string;
  quantity: number;
  bonoPrice: number;
  totalCost: number;
  interestRate: number;
  termDays: number;
  payoutPerBono: number;
  totalPayout: number;
  startDate: any;
  endDate: any;
  status: 'active' | 'completed' | 'cancelled';
  redeemedAt?: any;
  createdAt?: any;
}

// ─── CECOP — Centro de Control de Procesiones ────────────────────────────────
// Procesiones en el servidor con GPS en tiempo real: se asigna la cruz de guía y
// cada paso a un jugador, y su posición se sigue sobre el mapa del servidor.

/** Punto en coordenadas de Minecraft (bloques). */
export interface CecopPoint {
  x: number;
  z: number;
}

/** Recorte del mapa base que se muestra en el GPS, en % de la imagen. */
export interface CecopViewport {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Copia de la config del mapa del servidor en el momento de guardar la procesión.
 * Se duplica aquí porque `mod_map_config` es de lectura solo-admin y el GPS lo
 * tienen que ver todos los usuarios (y la fuente de OBS, sin sesión).
 */
export interface CecopMapSnapshot {
  imageUrl: string;
  calib: { xPct: number; yPct: number; mcX: number; mcZ: number }[];
}

/** Jugador asignado a un puesto de la procesión (cruz de guía o un paso). */
export interface CecopAsignacion {
  role: 'cruz' | 'paso';
  /** 0 para la cruz de guía; 1..numPasos para los pasos. */
  index: number;
  label: string;
  uuid: string;
  name: string;
}

export type CecopStatus = 'draft' | 'live' | 'finished';

export interface CecopProcesion {
  id: string;
  titulo: string;
  color: string;
  descripcion?: string;
  hermandadId?: string;
  numPasos: number;
  asignaciones: CecopAsignacion[];
  iglesia: CecopPoint | null;
  estacion: CecopPoint | null;
  ida: CecopPoint[];
  vuelta: CecopPoint[];
  viewport: CecopViewport | null;
  map: CecopMapSnapshot | null;
  status: CecopStatus;
  /** Muestra la tarjeta de la procesión en la home pública (sustituye al hero). */
  publicCard?: boolean;
  /** Directo de YouTube que se incrusta en la tarjeta pública. */
  youtubeUrl?: string;
  /** Token de la fuente de navegador para OBS (URL pública). */
  obsToken: string;
  startedAt?: any;
  finishedAt?: any;
  createdAt?: any;
  updatedAt?: any;
}

/** Posición en vivo de un puesto. La escribe `modHeartbeat` (cada ~15 s). */
export interface CecopLiveEntry extends CecopAsignacion {
  x?: number;
  z?: number;
  online: boolean;
  /**
   * Escudo que se pinta dentro del marcador (círculo negro con filete dorado).
   * Lo rellena el CLIENTE al pintar, no el latido: la web ya sabe de qué
   * hermandad es cada puesto y así el dato que viaja sigue siendo solo posición.
   */
  iconUrl?: string;
  /** Color del filete y del rastro del puesto (el de su hermandad). */
  ringColor?: string;
  /**
   * De qué hermandad es el puesto (solo el CECOP de Semana Santa, donde en el
   * mismo mapa hay varias). Lo usa el panel público para sacar el cuadro de
   * horarios de esa cofradía al apuntar su marcador.
   */
  hermandadId?: string;
}

export interface CecopLive {
  id: string;
  procesionId: string;
  entries: CecopLiveEntry[];
  updatedAt?: any;
}

// ─── CECOP SEMANA SANTA — un DÍA entero del programa ─────────────────────────
// A diferencia de `cecop_procesiones` (una procesión suelta seguida por
// JUGADORES), aquí se sigue un día del programa con TODAS sus cofradías, y lo
// que se sigue no son jugadores sino **entidades del servidor**: la entidad del
// paso (su UUID no cambia aunque se releve el costalero) y el cortejo (su
// primera fila ES la cruz de guía). Los recorridos NO se copian: salen de
// `dias_ss`, que es de lectura pública, así que un cambio en el programa se ve
// al momento en el GPS.

/** Un paso de una hermandad, atado a la entidad del servidor que lo lleva. */
export interface CecopDiaPaso {
  /** 1..n dentro de su hermandad. */
  index: number;
  label: string;
  /** UUID de la entidad de paso en el servidor (vacío = sin asignar). */
  entityUuid: string;
  /** Nombre legible de la entidad, copiado al asignarla. */
  entityName: string;
}

export interface CecopDiaHermandad {
  hermandadId: string;
  /** Copia del nombre: el latido escribe las etiquetas sin leer hermandades. */
  nombre: string;
  color: string;
  /**
   * Ajuste PROPIO de esta cofradía, en **% de la imagen**, que se SUMA al general
   * (`CecopDia.mapAjuste`). Sirve para arrastrar el recorrido de una sola hermandad
   * hasta encajarlo con las calles cuando el suyo, y solo el suyo, está corrido.
   *
   * Solo existe si se ha movido: una fila sin mover no lleva la clave (nunca
   * `undefined`, que el Admin SDK rechaza).
   */
  ajuste?: { dx: number; dy: number };
  /** Id del cortejo del servidor cuya PRIMERA FILA hace de cruz de guía. */
  cortejoId: number | null;
  cortejoLabel?: string;
  pasos: CecopDiaPaso[];
}

export interface CecopDia {
  /** Id del documento = id del día en `dias_ss`. */
  id: string;
  titulo: string;
  nombredia: string;
  tipo?: 'visperas' | 'semanasanta';
  fecha?: any;
  descripcion?: string;
  youtubeUrl?: string;
  /** ACTIVAR: enciende el seguimiento GPS y saca el panel en la home pública. */
  activo: boolean;
  map: CecopMapSnapshot | null;
  viewport: CecopViewport | null;
  /**
   * AJUSTE FINO del encaje de los recorridos sobre el mapa, en **% de la imagen**
   * (positivo = a la derecha / hacia abajo). Se suma a los puntos del programa
   * ANTES de convertirlos a coordenadas MC.
   *
   * Existe porque los recorridos del programa se trazan sobre el **mapa de ciudad**
   * (`city_map_config/main.mapImageUrl`) mientras que el CECOP los pinta sobre el
   * **mapa del servidor** (`mod_map_config/main.imageUrl`). Si las dos imágenes no
   * son EXACTAMENTE el mismo fichero (un reencuadre, otro margen), los trazados
   * salen desplazados y siempre en la misma dirección. Lo definitivo es subir la
   * misma imagen a los dos sitios; esto es la corrección para cuando no lo son.
   */
  mapAjuste?: { dx: number; dy: number } | null;
  hermandades: CecopDiaHermandad[];
  obsToken?: string;
  activadoAt?: any;
  createdAt?: any;
  updatedAt?: any;
}

export interface CecopDiaLiveEntry {
  hermandadId: string;
  role: 'cruz' | 'paso';
  index: number;
  label: string;
  x?: number;
  z?: number;
  online: boolean;
}

export interface CecopDiaLive {
  id: string;
  diaId: string;
  entries: CecopDiaLiveEntry[];
  updatedAt?: any;
}

/** Entidad de paso viva en el servidor, tal y como la reporta el latido del mod. */
export interface ModPasoEntity {
  /** UUID de la entidad: es lo que se guarda en la asignación. */
  id: string;
  name: string;
  type?: string;
  x: number;
  z: number;
  dim?: string;
  /** Jugador que lo lleva montado, si hay alguno (solo informativo). */
  rider?: string;
}

/** Cortejo vivo en el servidor (las formaciones no sobreviven a un reinicio). */
export interface ModCortejoInfo {
  id: number;
  filas?: number;
  owner?: string;
  x: number;
  z: number;
  dim?: string;
}

// ─── EL JARTI INGLÉS — tienda de enseres con entrega dentro del juego ────────
// El usuario compra en la web y recoge el paquete en el servidor principal con
// `/eljartiingles paquete`. Para pedir hace falta tener resuelta la cuenta de
// Minecraft (por la vinculación del banco o por la de actividad del servidor).

/** Un ítem del mod/juego que forma parte de un producto. */
export interface JartiItemRef {
  /** Registry name completo, ej. `example:cirio_rojo`. */
  id: string;
  count: number;
}

/** Entrada del catálogo de ítems que reporta el servidor (para el editor admin). */
export interface JartiCatalogItem {
  id: string;
  name: string;
}

export interface JartiProduct {
  id: string;
  name: string;
  description: string;
  /** Precio en oro (número, a diferencia de PackProduct que es texto libre). */
  price: number;
  imageUrl?: string;
  /** Galería opcional: imágenes adicionales de la ficha. */
  images?: string[];
  category?: string;
  /** Qué se entrega en el juego al recoger el paquete. */
  items?: JartiItemRef[];
  /** -1 = ilimitado. 0 = agotado. */
  stock?: number;
  /** Máximo por pedido (0 = sin límite). */
  maxPerOrder?: number;
  active?: boolean;
  featured?: boolean;
  order?: number;
  createdAt?: any;
  updatedAt?: any;
}

export type JartiOrderStatus = 'preparando' | 'listo' | 'entregado' | 'cancelado';

export interface JartiOrderLine {
  productId: string;
  name: string;
  imageUrl?: string;
  unitPrice: number;
  qty: number;
  items: JartiItemRef[];
}

export interface JartiOrderEvent {
  status: JartiOrderStatus | 'pedido';
  at: any;
  note?: string;
}

export interface JartiOrder {
  id: string;
  /** Código legible tipo `JI-7F3A2C`, el que se enseña al usuario. */
  code: string;
  uid: string;
  userName?: string;
  mcUuid: string;
  mcName: string;
  /** De dónde salió la cuenta de Minecraft: banco o actividad del servidor. */
  mcSource: 'bank' | 'activity';
  lines: JartiOrderLine[];
  total: number;
  currency: 'oro';
  accountId: string;
  accountIban?: string;
  status: JartiOrderStatus;
  createdAt?: any;
  /** Momento en que el pedido pasa a estar LISTO para recoger. */
  readyAt?: any;
  deliveredAt?: any;
  cancelledAt?: any;
  refunded?: boolean;
  tracking?: JartiOrderEvent[];
}

export interface JartiShopConfig {
  /** Minutos de preparación del pedido (por defecto 120 = 2 h). */
  preparationMinutes?: number;
  /** Si está cerrada no se admiten pedidos nuevos. */
  open?: boolean;
  /** Aviso destacado en la cabecera de la tienda. */
  announcement?: string;
  updatedAt?: any;
}

/** Resultado de resolver la cuenta de Minecraft del usuario actual. */
export interface JartiMcIdentity {
  found: boolean;
  mcUuid?: string;
  mcName?: string;
  source?: 'bank' | 'activity';
  message?: string;
}

// ─── GALERÍA PÚBLICA ────────────────────────────────────────────────────────
// Los eventos de la galería se gestionan desde /jartinet/admin/galeria y viven
// en `gallery_events`. Los eventos históricos siguen en `src/lib/data.ts`
// (fotos servidas desde /public) y se muestran detrás de estos.

export interface GalleryEventImage {
  id: string;
  src: string;
  alt?: string;
}

export interface GalleryEventDoc {
  id: string;
  title: string;
  /** Texto libre, ej. "Septiembre 2025". */
  date: string;
  modVersion?: string;
  /** Orden en la lista: mayor primero (los más nuevos arriba). */
  order?: number;
  images: GalleryEventImage[];
  createdAt?: any;
  updatedAt?: any;
}

// ─── ESTAMPITAS DEL MOD ─────────────────────────────────────────────────────
// Se crean desde /jartinet/admin/estampitas y las descarga el MOD (dentro de
// Minecraft) igual que el librito: la imagen NO va en el jar, así que se pueden
// añadir todas las que se quieran sin que el mod engorde ni penalice a un PC
// flojo (solo carga en VRAM la que se está mirando).
//
// El contrato con el mod es el fichero `estampitas/manifest.json` de Storage,
// que reconstruye la Cloud Function `estampitas_onChanged` en cada escritura.

export interface EstampitaDoc {
  id: string;
  /** Lo que dice el nazareno al dártela: «Toma una estampita de {nombre}». */
  nombre: string;
  /** URL pública de la imagen en Storage. */
  imageUrl: string;
  /** Hash del contenido: es el nombre del fichero y lo que evita re-descargas. */
  hash: string;
  /** Tamaño real del PNG ya normalizado (el mod la dibuja sin deformarla). */
  w?: number;
  h?: number;
  /** Orden en el catálogo y en la pestaña de creativo del mod. */
  order?: number;
  /** Una estampita desactivada desaparece del manifiesto (y del creativo del mod). */
  activa?: boolean;
  createdAt?: any;
  updatedAt?: any;
}

// ─── JARTIMARCHAS ───────────────────────────────────────────────────────────
// Las marchas que tocan las bandas DENTRO de Minecraft. Se suben desde
// /jartinet/jartimarchas (directores de banda y admins) y las aprueba un admin
// en /jartinet/admin/jartimarchas.
//
// Mismo diseño que el librito y las estampitas —el audio NO va en el jar, se
// descarga de la CDN y se cachea por hash de contenido— con una diferencia
// importante: aquí el catálogo lo vigila el SERVIDOR del mod y lo EMPUJA a los
// clientes en cuanto cambia, así que no hay que reconectar para oír una marcha
// nueva. El contrato con el juego es `marchas/manifest.json` en Storage.

export type MarchaEstado = 'pendiente' | 'aceptada' | 'rechazada';

export type MarchaOrigen = 'archivo' | 'youtube' | 'soundcloud';

/**
 * Qué es este audio dentro del repertorio de la banda.
 *
 * · `marcha` — una marcha del repertorio escrito de la banda. Va atada a una
 *   entrada concreta y solo puede haber un audio por entrada.
 * · `tambor` — pasos a tambor, redobles, etc. Nombre libre, sin atarse al
 *   repertorio.
 * · `random` — todo lo demás (avisos, cortes, lo que sea). Nombre libre.
 */
export type MarchaCategoria = 'marcha' | 'tambor' | 'random';

export const MARCHA_CATEGORIAS: { id: MarchaCategoria; etiqueta: string; plural: string }[] = [
  { id: 'marcha', etiqueta: 'Marcha del repertorio', plural: 'Marchas' },
  { id: 'tambor', etiqueta: 'Tambor', plural: 'Tambor' },
  { id: 'random', etiqueta: 'Random', plural: 'Random' },
];

export interface MarchaDoc {
  id: string;
  /** Banda de Jartinet a cuyo repertorio pertenece (id del documento). */
  bandaId: string;
  /** Copia del nombre de la banda, para listar sin cruzar colecciones. */
  bandaNombre: string;
  /** Nombre de la marcha, tal y como lo escribe quien la sube. */
  nombre: string;
  /** Qué es: marcha del repertorio, tambor o random. */
  categoria: MarchaCategoria;
  /**
   * Para `categoria: 'marcha'`, la entrada del repertorio escrito de la banda a la
   * que se ata (única). Para tambor y random, texto libre — normalmente el nombre.
   */
  repertorioItem: string;
  origen: MarchaOrigen;
  /** Enlace de YouTube original, si vino de ahí (informativo). */
  sourceUrl?: string;
  /** URL pública del OGG ya transcodificado. */
  audioUrl: string;
  /** Hash del contenido: es el nombre del fichero y lo que evita re-descargas. */
  hash: string;
  bytes: number;
  duracionMs: number;
  /**
   * RECORTE para el juego: desde qué milisegundo empieza a sonar y en cuál para
   * (`recorteFinMs` a 0 o ausente = hasta el final).
   *
   * **El OGG no se toca.** Se sigue sirviendo entero y direccionado por su contenido:
   * cortar el fichero cambiaría su hash y obligaría a TODOS los jugadores a volver a
   * descargarlo cada vez que se mueve el recorte un segundo. Lo que viaja al mod son
   * estos dos números dentro del manifiesto (`ini` / `fin`), y es él quien entra por
   * ahí y termina ahí. Se guardan con la CF `marchas_recorte`.
   */
  recorteIniMs?: number;
  recorteFinMs?: number;
  estado: MarchaEstado;
  motivoRechazo?: string;
  solicitadaPor: string;
  solicitadaPorNombre: string;
  solicitadaAt?: any;
  revisadaPor?: string;
  revisadaAt?: any;
}

/** Estado del manifiesto que consume el mod (lo escribe la Cloud Function). */
export interface MarchasManifestState {
  hash: string;
  bandas: number;
  marchas: number;
  bytes: number;
  generatedAt: number;
  manifestUrl?: string;
}

// ─── Sesiones de una banda ──────────────────────────────────────────────────
// Un guion de procesión: qué marchas suenan, en qué orden, a qué volumen y con
// cuánto solape entre ellas. Se monta en la web y se elige en el mod desde el
// mando de la banda.

export interface SesionPista {
  /** Id del documento de `marchas`. */
  marchaId: string;
  /** Copia del nombre, para pintar la lista sin cruzar colecciones. */
  nombre: string;
  /** Volumen 10–200 (%). Más volumen = se oye desde más lejos, no solo más fuerte. */
  volumen: number;
  /**
   * Cuántos milisegundos ANTES de que acabe la pista anterior empieza esta.
   * 0 = una detrás de otra. 5000 = se enlazan solapando cinco segundos.
   * En la primera pista no se usa.
   */
  solapeMs: number;
}

export interface SesionBanda {
  id: string;
  bandaId: string;
  bandaNombre: string;
  /** «Procesión Domingo de Ramos» */
  nombre: string;
  pistas: SesionPista[];
  creadaPor?: string;
  creadaAt?: any;
  actualizadaAt?: any;
}

/** Volumen por defecto de una pista y límites que acepta el backend. */
export const SESION_VOLUMEN_DEF = 100;
export const SESION_VOLUMEN_MIN = 10;
export const SESION_VOLUMEN_MAX = 200;
/** Tope de solape: más de 30 s dejaría de ser un enlace y sería una mezcla. */
export const SESION_SOLAPE_MAX_MS = 30000;

/** Resultado de transcodificar un audio, antes de solicitar el alta. */
export interface MarchaProcesada {
  audioUrl: string;
  hash: string;
  bytes: number;
  duracionMs: number;
  origen: MarchaOrigen;
  sourceUrl?: string;
  /** Título que trae YouTube, como sugerencia de nombre. */
  tituloSugerido?: string;
}
